import React, { forwardRef, useContext } from 'react';
import classNames from 'classnames';
import { T, L10NContext } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { StaticContext } from '@sm/webassets';
import { SurveyButton } from '~app/components/Survey/SurveyButton';
import generateEventData from '~app/pages/SurveyTaking/helper/generateEventDataForLogs';
import { SurveyContext } from '~app/pages/SurveyTaking/SurveyContext';
import useStyles from './useStyles';

const COPY = {
  PREVIOUS: {
    id: 'SurveyTaking.PageNavigation.Previous',
    defaultMessage: 'Previous',
    description: `[Type: button][Vis: high] - Allows the respondent to go back to the previous page to review or edit their response.`,
  },
  NEXT: {
    id: 'SurveyTaking.PageNavigation.Next',
    defaultMessage: 'Next',
    description: `[Type: button][Vis: high] - Saves the responses for that page in the Analyze Results section and proceeds to the next page in the survey.`,
  },
  DONE: {
    id: 'SurveyTaking.PageNavigation.Done',
    defaultMessage: 'Done',
    description: `[Type: button][Vis: high] - Submits the survey and saves the response as Complete in the Analyze Results section.`,
  },
};

export type Props = {
  /** Custom labels for Next, Previous, and Done buttons */
  labels?: {
    previous?: string;
    next?: string;
    done?: string;
  };
  /** The current page - starting at 1 */
  currentPage: number;
  /** The total number of pages */
  totalPages: number;
  /** Callback for when the user clicks the Next button */
  onNext(): void;
  /** Callback for when the user clicks the Previous button */
  onPrevious(): void;
  /** Callback for when the user clicks the Done button */
  onDone(): void;
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * Determine if a translation attribute applies if a user-generated label
 * is not provided
 * @param label - A user-generated label
 * @param defaultLabel - a default label
 * @returns {Object} and object with a label and translation details
 */
function getButtonDetails(
  label?: React.ReactNode,
  defaultLabel?: React.ReactNode
): { label?: React.ReactNode; translate?: 'yes' | 'no' } {
  return { label: label ?? defaultLabel, translate: label ? 'no' : undefined };
}

const PageNavigation = (
  { labels, className, currentPage, totalPages = 1, onNext, onPrevious, onDone }: Props,
  ref: React.Ref<HTMLDivElement>
): JSX.Element => {
  const { isRTL } = useContext(L10NContext);
  const { pageNavigationContainer, navButtons } = useStyles({ isRTL });
  const prevButton = getButtonDetails(labels?.previous, <T desc={COPY.PREVIOUS} />);
  const doneButton = getButtonDetails(labels?.done, <T desc={COPY.DONE} />);
  const nextButton = getButtonDetails(labels?.next, <T desc={COPY.NEXT} />);
  const { environment, user } = useContext(StaticContext);
  const { survey, collector, activePage } = useContext(SurveyContext);
  const otherEventData = generateEventData({ environment, user, collector, survey, currentPage: activePage });

  return (
    <div className={classNames(pageNavigationContainer, className)}>
      {currentPage > 1 && (
        <SurveyButton translate={prevButton.translate} className={navButtons} size="lg" onClick={onPrevious}>
          <>{prevButton.label}</>
        </SurveyButton>
      )}
      {currentPage === totalPages && (
        <SurveyButton
          translate={doneButton.translate}
          className={navButtons}
          size="lg"
          onClick={onDone}
          data-sm-metrics={generateMetricsAttribute({
            data: {
              actionType: 'final_submit',
              actionFlow: 'survey_completed',
              ...otherEventData,
            },
          })}
        >
          <>{doneButton.label}</>
        </SurveyButton>
      )}
      {currentPage < totalPages && (
        <SurveyButton translate={nextButton.translate} className={navButtons} size="lg" onClick={onNext}>
          <>{nextButton.label}</>
        </SurveyButton>
      )}
    </div>
  );
};

export default forwardRef<HTMLDivElement, Props>(PageNavigation);
